import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Slot group definitions — inline literals for the static schema extractor.
// ============================================================================

// Pet stock imagery — Unsplash hotlinks so the theme stays self-contained.
// Editors can override per-site by picking a property photo in the media dialog.
const heroImageDef: SlotGroupDefinition = {
  label: 'Pet Policy Hero Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=1200&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Tabby cat resting comfortably indoors',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const feature1ImageDef: SlotGroupDefinition = {
  label: 'Pet Amenity #1 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&w=1000&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Dog being brushed at a pet spa',
    },
  },
};

const feature2ImageDef: SlotGroupDefinition = {
  label: 'Pet Amenity #2 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1477884213360-7e9d7dcc1e48?auto=format&fit=crop&w=1000&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Dog playing in a grassy dog park',
    },
  },
};

const feature3ImageDef: SlotGroupDefinition = {
  label: 'Pet Amenity #3 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=1000&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Dog sitting beside its owner at an outdoor table',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  const heroImage = useSlotGroup('petpolicy.hero_image', heroImageDef);
  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);
  const feature1Image = useSlotGroup('petpolicy.feature_1_image', feature1ImageDef);
  const feature2Image = useSlotGroup('petpolicy.feature_2_image', feature2ImageDef);
  const feature3Image = useSlotGroup('petpolicy.feature_3_image', feature3ImageDef);

  useRevealStyles();
  useSiteCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up bg-[var(--vibe-color-background)] py-12 md:py-20"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* ── Top: hero image + heading/copy/CTA ── */}
        <div className="pp-hero">
          <div className="pp-hero-media">
            <EditableSlotGroup
              groupPath="petpolicy.hero_image"
              definition={heroImageDef}
              className="pp-hero-media-slot"
            >
              {heroImage.image ? (
                <VibesitesImage
                  url={heroImage.image}
                  alt={heroImage.alt || ''}
                  sizes="(min-width: 1024px) 40rem, 100vw"
                  className="pp-hero-img"
                />
              ) : (
                <div className="pp-hero-img pp-hero-img--empty" />
              )}
            </EditableSlotGroup>
          </div>

          <div className="pp-hero-content">
            <RichTextContent
              path="petpolicy.heading"
              label="Heading"
              as="h2"
              className="pp-heading"
            />
            <RichTextContent
              path="petpolicy.description"
              label="Description"
              as="div"
              className="pp-description"
            />
            <EditableSlotGroup
              groupPath="petpolicy.cta_link"
              definition={ctaLinkDef}
              className="pp-cta-wrap"
            >
              <a
                href={ctaLink.url || '/contact'}
                target={ctaLink._target || '_self'}
                className="site-cta site-cta--secondary"
              >
                {ctaLink.label || 'Contact Us'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* ── Bottom: three feature cards (image + title + description) ── */}
        <div className="pp-features">
          <article className="pp-feature">
            <EditableSlotGroup
              groupPath="petpolicy.feature_1_image"
              definition={feature1ImageDef}
              className="pp-feature-media"
            >
              {feature1Image.image ? (
                <VibesitesImage
                  url={feature1Image.image}
                  alt={feature1Image.alt || ''}
                  sizes="(min-width: 1024px) 26rem, (min-width: 640px) 50vw, 100vw"
                  className="pp-feature-img"
                />
              ) : (
                <div className="pp-feature-img pp-feature-img--empty" />
              )}
            </EditableSlotGroup>
            <RichTextContent
              path="petpolicy.feature_1_title"
              label="Amenity #1 Title"
              as="h3"
              className="pp-feature-title"
            />
            <RichTextContent
              path="petpolicy.feature_1_description"
              label="Amenity #1 Description"
              as="div"
              className="pp-feature-description"
            />
          </article>

          <article className="pp-feature">
            <EditableSlotGroup
              groupPath="petpolicy.feature_2_image"
              definition={feature2ImageDef}
              className="pp-feature-media"
            >
              {feature2Image.image ? (
                <VibesitesImage
                  url={feature2Image.image}
                  alt={feature2Image.alt || ''}
                  sizes="(min-width: 1024px) 26rem, (min-width: 640px) 50vw, 100vw"
                  className="pp-feature-img"
                />
              ) : (
                <div className="pp-feature-img pp-feature-img--empty" />
              )}
            </EditableSlotGroup>
            <RichTextContent
              path="petpolicy.feature_2_title"
              label="Amenity #2 Title"
              as="h3"
              className="pp-feature-title"
            />
            <RichTextContent
              path="petpolicy.feature_2_description"
              label="Amenity #2 Description"
              as="div"
              className="pp-feature-description"
            />
          </article>

          <article className="pp-feature">
            <EditableSlotGroup
              groupPath="petpolicy.feature_3_image"
              definition={feature3ImageDef}
              className="pp-feature-media"
            >
              {feature3Image.image ? (
                <VibesitesImage
                  url={feature3Image.image}
                  alt={feature3Image.alt || ''}
                  sizes="(min-width: 1024px) 26rem, (min-width: 640px) 50vw, 100vw"
                  className="pp-feature-img"
                />
              ) : (
                <div className="pp-feature-img pp-feature-img--empty" />
              )}
            </EditableSlotGroup>
            <RichTextContent
              path="petpolicy.feature_3_title"
              label="Amenity #3 Title"
              as="h3"
              className="pp-feature-title"
            />
            <RichTextContent
              path="petpolicy.feature_3_description"
              label="Amenity #3 Description"
              as="div"
              className="pp-feature-description"
            />
          </article>
        </div>
      </div>

      <style>{`
        /* ── Top hero: image left, copy right on desktop ── */
        .pp-hero {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
        }
        @media (min-width: 768px) {
          .pp-hero {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 4rem;
          }
        }

        .pp-hero-media {
          position: relative;
          width: 100%;
          aspect-ratio: 5 / 3;
          overflow: hidden;
          border-radius: var(--vibe-radius-md, 0.25rem);
        }
        .pp-hero-media-slot {
          position: absolute;
          inset: 0;
        }
        .pp-hero-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .pp-hero-img--empty {
          background: var(--vibe-color-surface);
        }

        .pp-hero-content {
          min-width: 0;
        }
        .pp-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2.25rem, 4.5vw, 3.5rem);
          font-weight: 700;
          line-height: 1.05;
          color: var(--vibe-color-primary);
          margin: 0 0 1.5rem;
        }
        .pp-description {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
          margin: 0 0 2rem;
          max-width: 34rem;
        }
        .pp-description [data-vibesite-richtext] p {
          margin: 0 0 1em;
        }
        .pp-description [data-vibesite-richtext] p:last-child {
          margin-bottom: 0;
        }
        .pp-description [data-vibesite-richtext] ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 1em;
        }
        .pp-description [data-vibesite-richtext] a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .pp-cta-wrap {
          display: inline-block;
        }

        /* ── Feature cards row ── */
        .pp-features {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-top: 4rem;
        }
        @media (min-width: 640px) {
          .pp-features {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (min-width: 1024px) {
          .pp-features {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 2.25rem;
            margin-top: 5rem;
          }
        }

        .pp-feature {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .pp-feature-media {
          position: relative;
          display: block;
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          border-radius: var(--vibe-radius-md, 0.25rem);
          margin-bottom: 1.25rem;
        }
        .pp-feature-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .pp-feature-img--empty {
          background: var(--vibe-color-surface);
        }
        .pp-feature-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.2;
          color: var(--vibe-color-primary);
          margin: 0 0 0.75rem;
        }
        .pp-feature-title [data-vibesite-richtext] p {
          margin: 0;
        }
        .pp-feature-description {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.6;
          color: var(--vibe-color-text);
        }
        .pp-feature-description [data-vibesite-richtext] p {
          margin: 0 0 0.75em;
        }
        .pp-feature-description [data-vibesite-richtext] p:last-child {
          margin-bottom: 0;
        }
        .pp-feature-description [data-vibesite-richtext] a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* ── Mobile: center text content ── */
        @media (max-width: 767px) {
          .pp-hero-content {
            text-align: center;
          }
          .pp-description {
            margin-left: auto;
            margin-right: auto;
          }
          .pp-feature {
            text-align: center;
            align-items: center;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Pet policy</p>',
    },
    'petpolicy.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span> we understand that pets are part of your lifestyle. That&#39;s why our community welcomes pets in our apartment homes with a pet policy designed to support both residents and their companions.</p>',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicy.feature_1_title': {
      label: 'Amenity #1 Title',
      type: 'richtext',
      defaultValue: '<p>Pet spa</p>',
    },
    'petpolicy.feature_1_description': {
      label: 'Amenity #1 Description',
      type: 'richtext',
      defaultValue:
        '<p>Treat your furry roommate to some well-deserved pampering at our on-site pet spa. From rinse stations to grooming areas, it&#39;s the perfect spot to keep your pet fresh and clean right at home.</p>',
    },
    'petpolicy.feature_2_title': {
      label: 'Amenity #2 Title',
      type: 'richtext',
      defaultValue: '<p>Dog park</p>',
    },
    'petpolicy.feature_2_description': {
      label: 'Amenity #2 Description',
      type: 'richtext',
      defaultValue:
        '<p>Let your pup burn off energy and socialize at our fenced-in dog park, just steps from your apartment. It&#39;s a convenient and secure space for students and their pets to enjoy daily playtime outdoors.</p>',
    },
    'petpolicy.feature_3_title': {
      label: 'Amenity #3 Title',
      type: 'richtext',
      defaultValue: '<p>Pet-friendly restaurants</p>',
    },
    'petpolicy.feature_3_description': {
      label: 'Amenity #3 Description',
      type: 'richtext',
      defaultValue:
        '<p>Explore local pet-friendly restaurants near <span data-type="field-token" data-field="property_name"></span>, where you and your four-legged friend can grab a bite together.</p>',
    },
  },
  slotGroups: {
    'petpolicy.hero_image': heroImageDef,
    'petpolicy.cta_link': ctaLinkDef,
    'petpolicy.feature_1_image': feature1ImageDef,
    'petpolicy.feature_2_image': feature2ImageDef,
    'petpolicy.feature_3_image': feature3ImageDef,
  },
};

(PetPolicy as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicy;
